import type { Company, User } from "src/api/client-api/useUserNew";

export type PlatformPermissionTuple = readonly [string, string];
export type OrganizationPermissions = Readonly<Record<string, readonly string[]>>;

const permissionIdentifierCandidates = (slug: string, entity: string): string[] => [
    `${slug} ${entity}`,
    `${slug}_${entity}`,
];

export const hasPlatformScopedPermission = (
    permissions: readonly PlatformPermissionTuple[],
    slug: string,
    entity: string,
): boolean => {
    const target = [slug, entity];

    return permissions.some((permission) => permission.every((element, index) => element === target[index]));
};

export const hasOrganizationPermission = (
    permissionIdentifiers: readonly string[],
    slug: string,
    entity: string,
): boolean => {
    const granted = new Set(permissionIdentifiers);

    return permissionIdentifierCandidates(slug, entity).some((identifier) => granted.has(identifier));
};

export const hasScopedOrganizationPermission = (
    organizationPermissions: OrganizationPermissions,
    companyId: number,
    slug: string,
    entity: string,
): boolean => {
    const permissionIdentifiers = organizationPermissions[String(companyId)];

    if (permissionIdentifiers === undefined) {
        return false;
    }

    return hasOrganizationPermission(permissionIdentifiers, slug, entity);
};

export const hasOrganizationOrPlatformScopedPermission = (
    organizationPermissions: OrganizationPermissions,
    companyId: number | null,
    slug: string,
    entity: string,
    platformPermissions: readonly PlatformPermissionTuple[],
): boolean => {
    if (hasPlatformScopedPermission(platformPermissions, slug, entity)) {
        return true;
    }

    if (companyId === null) {
        return false;
    }

    return hasScopedOrganizationPermission(organizationPermissions, companyId, slug, entity);
};

export const hasCompanyScopedAccess = (organizationPermissions: OrganizationPermissions, companyId: number): boolean =>
    (organizationPermissions[String(companyId)]?.length ?? 0) > 0;

export const hasPermissionOnAnyOrganization = (
    organizationPermissions: OrganizationPermissions,
    slug: string,
    entity: string,
): boolean =>
    Object.values(organizationPermissions).some((permissionIdentifiers) =>
        hasOrganizationPermission(permissionIdentifiers, slug, entity),
    );

export const hasPermissionOnPlatform = (user: User, slug: string, entity: string): boolean =>
    user.roles.includes("ROLE_ADMIN") || hasPlatformScopedPermission(user.permissions, slug, entity);

export const hasPermissionOnPlatformOrAnyOrganization = (user: User, slug: string, entity: string): boolean =>
    hasPermissionOnPlatform(user, slug, entity) ||
    hasPermissionOnAnyOrganization(buildOrganizationPermissions(user.organizations), slug, entity);

export const buildOrganizationPermissions = (
    organizations: ReadonlyArray<{ id: number; permissions: readonly string[] }>,
): OrganizationPermissions =>
    Object.fromEntries(organizations.map((organization) => [String(organization.id), organization.permissions]));

export const getSelectableCompanies = (user: User): Company[] => {
    if (user.roles.includes("ROLE_ADMIN")) {
        return user.organizations;
    }

    const organizationPermissions = buildOrganizationPermissions(user.organizations);

    return user.organizations.filter((company) => hasCompanyScopedAccess(organizationPermissions, company.id));
};
